import { useEffect, useState, useRef } from "react";
import GreCaptcha from "../components/gReCaptcha";
import { Formik, Form, Field, ErrorMessage, type FormikProps } from "formik";
import type { ErrorMessageProps } from "formik";
import { useLocation, useNavigate } from "react-router";
import Swal from "sweetalert2";
import * as Yup from "yup";

// Typed Hooks
import { useAppSelector, useAppDispatch } from "src/store/hooks";

// Actions
import { userLogin, sendRecoveryRequest, getPaDetails } from "src/store/auth/actions";

// Assets
import Logo from "src/assets/images/icom-logo-branco.png";
import IcomInput from "src/components/formik/icom-input";
import { paDetailsLoaded } from "src/store/auth/reducer";
import { setQueues } from "src/store/config/reducer";
import { checkExtension, isDev, isStage } from "src/helpers";
import { trackEvent } from "src/services/logger";

const CustomErrorMessage = (props: ErrorMessageProps) => (
  <ErrorMessage {...props}>
    {(msg) => (
      <div role="alert" data-testid="error" className="error-message text-red-400 text-xs mt-2 font-sora flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none" className="mr-1">
          <path
            d="M9.99998 6.49998L5.99998 10.5M5.99998 6.49998L9.99998 10.5M14.6666 8.49998C14.6666 12.1819 11.6819 15.1666 7.99998 15.1666C4.31808 15.1666 1.33331 12.1819 1.33331 8.49998C1.33331 4.81808 4.31808 1.83331 7.99998 1.83331C11.6819 1.83331 14.6666 4.81808 14.6666 8.49998Z"
            stroke="#EB5636"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {msg}
      </div>
    )}
  </ErrorMessage>
);

const ForgotPassword = ({ backHandler, email }: { backHandler: React.MouseEventHandler<HTMLButtonElement>; email: string }) => {
  // State
  const [state, setState] = useState({
    loading: false,
    sent: false,
    email: "",
  });

  // Methods
  const doRecovery = async (values: { email: string }) => {
    setState((s) => ({ ...s, loading: true }));

    const { success } = await sendRecoveryRequest(values.email);

    if (success) trackEvent("password_recovery_requested");

    setState((s) => ({
      ...s,
      email: values.email,
      sent: success,
    }));
  };

  // Validations
  const validationSchema = Yup.object({
    email: Yup.string().email("Digite um e-mail válido").required("Digite um e-mail"),
  });

  return !state.sent ? (
    <div className="forgot-password max-w-[420px] w-full">
      <h2 className="text-3xl font-bold font-sora mb-3">Esqueceu sua senha?</h2>

      <p className="mb-10 block font-normal text-neutral-gray-500">Informe o e-mail que utiliza para acessar a a plataforma</p>

      <Formik
        validationSchema={validationSchema}
        initialValues={{
          email,
        }}
        onSubmit={doRecovery}
      >
        <Form>
          <div data-testid="forgot-password-form" className="mb-6 relative">
            <label htmlFor="email" className="block mb-2 text-sm text-neutral-gray-600 font-bold">
              E-mail
            </label>
            <Field
              data-testid="email-input"
              type="email"
              placeholder="Digite o e-mail"
              name="email"
              component={IcomInput}
              icon={({ className }: { className: string }) => (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none" className={className}>
                  <path
                    d="M17.9167 15.5L12.381 10.5M7.61913 10.5L2.08344 15.5M1.66675 6.33337L8.47085 11.0962C9.02182 11.4819 9.29731 11.6748 9.59697 11.7495C9.86166 11.8154 10.1385 11.8154 10.4032 11.7495C10.7029 11.6748 10.9783 11.4819 11.5293 11.0962L18.3334 6.33337M5.66675 17.1667H14.3334C15.7335 17.1667 16.4336 17.1667 16.9684 16.8942C17.4388 16.6545 17.8212 16.2721 18.0609 15.8017C18.3334 15.2669 18.3334 14.5668 18.3334 13.1667V7.83337C18.3334 6.43324 18.3334 5.73318 18.0609 5.1984C17.8212 4.72799 17.4388 4.34554 16.9684 4.10586C16.4336 3.83337 15.7335 3.83337 14.3334 3.83337H5.66675C4.26662 3.83337 3.56655 3.83337 3.03177 4.10586C2.56137 4.34554 2.17892 4.72799 1.93923 5.1984C1.66675 5.73318 1.66675 6.43324 1.66675 7.83337V13.1667C1.66675 14.5668 1.66675 15.2669 1.93923 15.8017C2.17892 16.2721 2.56137 16.6545 3.03177 16.8942C3.56655 17.1667 4.26662 17.1667 5.66675 17.1667Z"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            />

            <CustomErrorMessage name="email" />
          </div>

          <button
            role="button"
            type="submit"
            data-testid="sent-forgot-password-button"
            className="group mb-8 ease-in-out transition-all relative flex w-full justify-center rounded-md bg-primary-blue-500 px-3 py-3 font-semibold text-white hover:bg-blue-600 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            disabled={state.loading}
          >
            {state.loading ? "Enviando..." : "Enviar"}
          </button>
        </Form>
      </Formik>

      <button type="button" className="text-neutral-gray-400" onClick={backHandler}>
        <svg className="inline-block mr-2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M16.6667 10H3.33337M3.33337 10L8.33337 15M3.33337 10L8.33337 5" stroke="#B3B4C2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Voltar
      </button>
    </div>
  ) : (
    <div data-testid="forgot-password-success" className="forgot-password-success max-w-[420px] w-full">
      <h2 className="text-3xl font-bold font-sora mb-3">Verifique seu e-mail</h2>
      <p className="mb-10 block font-normal text-neutral-gray-500">Enviamos um e-mail para {state.email}.Clique no botão abaixo e acesse a plataforma com a senha que foi enviada.</p>
      <button
        role="button"
        type="button"
        className="group mb-8 ease-in-out transition-all relative flex w-full justify-center rounded-md bg-primary-blue-500 px-3 py-3 font-semibold text-white hover:bg-blue-600 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        onClick={backHandler}
      >
        Ir para o login
      </button>
    </div>
  );
};

export default function Login() {
  const [state, setState] = useState({
    showForgot: false,
    forgotMail: "",
  });

  // Navigation
  const navigate = useNavigate();
  const navigation = useLocation();

  // Redux
  const { loading, error, isLoggedIn } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const validationSchema = Yup.object({
    email: Yup.string().email("Digite um e-mail válido").required("Digite um e-mail"),
    password: Yup.string().required("Digite a senha"),
    ...(!isStage ? { recaptcha: Yup.string().required("É necessário validar o reCaptcha") } : {}),
  });

  // Form ref
  type FormValues = {
    email: string;
    password: string;
    recaptcha: string;
  };

  const formRef = useRef<FormikProps<FormValues>>(null);

  // Private methods
  const doLogin = async (values: Yup.InferType<typeof validationSchema>) => {
    const result = await dispatch(userLogin(values));

    if (result.type === "auth/login/fulfilled") {
      const paDetailsRequest = await getPaDetails();

      if (paDetailsRequest.success) {
        dispatch(paDetailsLoaded({ ...paDetailsRequest.data }));
      }

      if (paDetailsRequest.data.fila) {
        dispatch(setQueues(paDetailsRequest.data.fila));
      }
    }
  };

  useEffect(() => {
    async function init() {
      const extensionEnabled = await checkExtension();

      if (!extensionEnabled && !isStage) {
        await Swal.fire({
          title: "Extensão não instalada!",
          html: `Não foi detectada a extensão ICOM, para prosseguir favor instalar clicando <a href="https://chromewebstore.google.com/detail/okafgffdfbfkfhpgdalfammhnkbkabon/" target="_blank" style="color: blue;">aqui.</a> 
                  Após instalar clique no botão recarregar para prosseguir.`,
          confirmButtonText: "Recarregar página",
        });

        if (!isDev()) window.location.reload();
      }

      const hasError = navigation.state?.authError;

      if (hasError) {
        Swal.fire({
          title: hasError,
          icon: "error",
        });

        window.history.replaceState({}, document.title);
      }
    }

    init();
  }, []);

  // redirect authenticated user to profile screen
  useEffect(() => {
    if (isLoggedIn) {
      navigate("/");
    }
  }, [navigate, isLoggedIn]);

  // Render
  return (
    <section className="page auth login h-screen flex flex-row">
      <div className="blue-area w-[560px] bg-primary-blue-500 p-[56px] flex flex-col justify-between">
        <img src={Logo} className="mb-5 max-w-[88px]" />
        <div className="sub-infos text-white max-w-[350px]">
          <h5 className="font-bold font-sora mb-1 text-2xl">Softphone</h5>
          <p>Sistema para atendimento e controle diário dos intérpretes do ICOM</p>
        </div>
      </div>
      <div className="form-area flex-1 flex items-center justify-center">
        {!state.showForgot ? (
          <Formik
            innerRef={formRef}
            validationSchema={validationSchema}
            initialValues={{
              email: "",
              password: "",
              recaptcha: "",
            }}
            onSubmit={doLogin}
          >
            {(props) => (
              <form data-testid="login-form" onSubmit={props.handleSubmit} className="max-w-[420px] w-full">
                <h2 className="text-3xl font-bold font-sora mb-10">
                  Olá,
                  <br />
                  acesse sua conta
                </h2>

                <div className="mb-6 relative">
                  <label htmlFor="email" className="block mb-2 text-sm text-neutral-gray-600 font-bold">
                    E-mail
                  </label>
                  <Field
                    data-testid="email-input"
                    type="email"
                    placeholder="Digite o e-mail"
                    name="email"
                    component={IcomInput}
                    icon={({ className }: { className: string }) => (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none" className={className}>
                        <path
                          d="M17.9167 15.5L12.381 10.5M7.61913 10.5L2.08344 15.5M1.66675 6.33337L8.47085 11.0962C9.02182 11.4819 9.29731 11.6748 9.59697 11.7495C9.86166 11.8154 10.1385 11.8154 10.4032 11.7495C10.7029 11.6748 10.9783 11.4819 11.5293 11.0962L18.3334 6.33337M5.66675 17.1667H14.3334C15.7335 17.1667 16.4336 17.1667 16.9684 16.8942C17.4388 16.6545 17.8212 16.2721 18.0609 15.8017C18.3334 15.2669 18.3334 14.5668 18.3334 13.1667V7.83337C18.3334 6.43324 18.3334 5.73318 18.0609 5.1984C17.8212 4.72799 17.4388 4.34554 16.9684 4.10586C16.4336 3.83337 15.7335 3.83337 14.3334 3.83337H5.66675C4.26662 3.83337 3.56655 3.83337 3.03177 4.10586C2.56137 4.34554 2.17892 4.72799 1.93923 5.1984C1.66675 5.73318 1.66675 6.43324 1.66675 7.83337V13.1667C1.66675 14.5668 1.66675 15.2669 1.93923 15.8017C2.17892 16.2721 2.56137 16.6545 3.03177 16.8942C3.56655 17.1667 4.26662 17.1667 5.66675 17.1667Z"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  />

                  <CustomErrorMessage name="email" />
                </div>

                <div className="mb-8 relative">
                  <label htmlFor="password" className="block mb-2 text-sm text-neutral-gray-600 font-bold">
                    Senha
                  </label>
                  <Field
                    data-testid="password"
                    type="password"
                    placeholder="Digite a senha"
                    name="password"
                    icon={({ className }: { className: string }) => (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none" className={className}>
                        <path
                          d="M14.1667 8.83333V7.16667C14.1667 4.86548 12.3012 3 10 3C7.69885 3 5.83337 4.86548 5.83337 7.16667V8.83333M10 12.5833V14.25M7.33337 18H12.6667C14.0668 18 14.7669 18 15.3017 17.7275C15.7721 17.4878 16.1545 17.1054 16.3942 16.635C16.6667 16.1002 16.6667 15.4001 16.6667 14V12.8333C16.6667 11.4332 16.6667 10.7331 16.3942 10.1984C16.1545 9.72795 15.7721 9.3455 15.3017 9.10582C14.7669 8.83333 14.0668 8.83333 12.6667 8.83333H7.33337C5.93324 8.83333 5.23318 8.83333 4.6984 9.10582C4.22799 9.3455 3.84554 9.72795 3.60586 10.1984C3.33337 10.7331 3.33337 11.4332 3.33337 12.8333V14C3.33337 15.4001 3.33337 16.1002 3.60586 16.635C3.84554 17.1054 4.22799 17.4878 4.6984 17.7275C5.23318 18 5.93324 18 7.33337 18Z"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    component={IcomInput}
                  />
                  <CustomErrorMessage name="password" />
                </div>

                <div className="mb-8 relative flex flex-col items-center">
                  {!isStage && <GreCaptcha verifyCallback={(res) => props.setFieldValue("recaptcha", res)} />}
                  <CustomErrorMessage name="recaptcha" />
                </div>

                <button
                  data-testid="submit-button"
                  type="submit"
                  className="group mb-8 ease-in-out transition-all relative flex w-full justify-center rounded-md bg-primary-blue-500 px-3 py-3 font-semibold text-white hover:bg-blue-600 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  disabled={loading}
                >
                  {loading ? "Entrando..." : "Entrar"}
                </button>

                <button
                  type="button"
                  data-testid="forgot-password-button"
                  className="m-auto block text-neutral-gray-400 hover:text-neutral-500"
                  onClick={() =>
                    setState((s) => ({
                      ...s,
                      showForgot: true,
                      forgotMail: formRef.current ? formRef.current.values.email : "",
                    }))
                  }
                >
                  Esqueci minha senha
                </button>

                {error && <span className="bg-red-400 text-white p-2 text-sm rounded-md mt-4 block">Erro: {error}</span>}
              </form>
            )}
          </Formik>
        ) : (
          <ForgotPassword email={state.forgotMail} backHandler={() => setState((s) => ({ ...s, showForgot: false }))} />
        )}
      </div>
    </section>
  );
}
